import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  saveQuarterlyPreread,
  generateQuarterlyPreread,
  DEFAULT_META,
} from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import {
  rememberQuarterlyPreread,
  clearQuarterlyPrereadOverride,
} from '../data/quarterlyPrereadOverride.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSaveQuarterlyPreread — save (or clear) the meta.quarterlyPreread
 * slot via the server-side partial update.
 */
export function useSaveQuarterlyPreread() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const preread = await saveQuarterlyPreread(payload)
      return preread
    },

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META
      clearQuarterlyPrereadOverride()

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Quarterly pre-read',
          queryClient.getQueryData(['goals']),
          previous
        )

      const next = payload?.clear ? null : payload
      queryClient.setQueryData(['meta'], {
        ...previous,
        quarterlyPreread: next,
      })
      return { previous }
    },

    onSuccess: (preread) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], { ...current, quarterlyPreread: preread })
      rememberQuarterlyPreread(preread ?? null)
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}

/**
 * useGenerateQuarterlyPreread — POST to /ai-quarterly-preread with the
 * week's inputs; receive { topLine, goalHealth[], highlights[],
 * needsAttention[] }. Does NOT auto-save — caller decides.
 */
export function useGenerateQuarterlyPreread() {
  const mutation = useMutation({
    mutationFn: async (args) => generateQuarterlyPreread(args || {}),
  })
  return withArchiveGuard(mutation)
}
